/* A place selection belongs to this profile and this exact input value. */
(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else Object.defineProperty(root, "LouderrProfilePlaces", {value: Object.freeze(api)});
})(typeof window === "undefined" ? null : window, function () {
  "use strict";
  function create({root, api, getIdentity, getSavedLocation = () => "", onChange = () => {}, debounceMs = 220, clock = globalThis}) {
    const input = root.querySelector("input");
    const list = root.querySelector("[role=listbox]");
    const status = root.querySelector("[role=status]");
    const error = root.querySelector("[role=alert]");
    const retry = root.querySelector("[data-place-retry]");
    let identity = null, saved = "", selected = null, places = [], active = -1;
    let revision = 0, timer = null, request = null, composing = false, validationError = false;
    const same = (a, b) => a?.viewerId === b?.viewerId && a?.csrf === b?.csrf && a?.social === b?.social;
    const text = () => input.value.trim();
    const searchable = () => !!identity?.viewerId && !input.disabled && text().length >= 1 && text().length <= 80;
    const live = op => op.revision === revision && same(op.identity, getIdentity()) && input.value === op.value && !input.disabled;

    function failure(message = "", invalid = false) {
      validationError = invalid;
      error.textContent = message; error.hidden = !message;
      input.setAttribute("aria-invalid", invalid ? "true" : "false");
    }
    function stop() {
      revision++;
      if (timer !== null) clock.clearTimeout(timer);
      timer = null; request?.abort(); request = null;
      input.setAttribute("aria-busy", "false");
    }
    function close() {
      places = []; active = -1; list.replaceChildren(); list.hidden = true;
      input.setAttribute("aria-expanded", "false"); input.removeAttribute("aria-activedescendant");
    }
    function dismiss() {
      stop(); close(); status.textContent = ""; retry.hidden = true;
    }
    function load(value) {
      dismiss(); saved = typeof value === "string" ? value.trim() : "";
      selected = null; input.value = saved; failure();
    }
    function syncIdentity() {
      const next = getIdentity();
      if (same(identity, next)) return false;
      identity = next; load(next?.viewerId ? getSavedLocation() : "");
      return true;
    }
    function highlight(index) {
      active = index;
      [...list.children].forEach((option, i) => option.setAttribute("aria-selected", i === index ? "true" : "false"));
      const option = list.children[index];
      if (option) {
        input.setAttribute("aria-activedescendant", option.id);
        option.scrollIntoView?.({block: "nearest"});
      } else input.removeAttribute("aria-activedescendant");
    }
    function choose(place, op) {
      if (!live(op)) return;
      dismiss(); selected = place; input.value = place.label; failure();
      status.textContent = `${place.label} selected.`;
      onChange(); input.focus({preventScroll: true});
    }
    function render(op, activate) {
      list.replaceChildren();
      places.forEach((place, index) => {
        const option = root.ownerDocument.createElement("li");
        option.id = `${list.id}-${index}`;
        option.setAttribute("role", "option"); option.setAttribute("aria-selected", "false");
        option.textContent = place.label;
        // Keep focus on the combobox so a tap does not dismiss the list before click.
        option.addEventListener("pointerdown", event => { if (event.button === 0) event.preventDefault(); });
        option.addEventListener("click", () => choose(place, op));
        list.append(option);
      });
      list.hidden = !places.length; input.setAttribute("aria-expanded", places.length ? "true" : "false");
      highlight(activate === "first" ? 0 : activate === "last" ? places.length - 1 : -1);
      status.textContent = places.length ? `${places.length} places found.` : "No matching places. Try a nearby city, or leave Location blank.";
    }
    async function search(activate) {
      syncIdentity(); stop(); close(); retry.hidden = true;
      if (!searchable() || composing) { status.textContent = ""; return; }
      const op = {identity, revision, value: input.value};
      const controller = request = new AbortController();
      input.setAttribute("aria-busy", "true"); status.textContent = "Finding places…";
      try {
        const result = await api(`/api/account/places?q=${encodeURIComponent(text())}`, {signal: controller.signal});
        if (!live(op)) return;
        if (!Array.isArray(result?.places) || result.places.some(place => !place || typeof place.id !== "string"
          || !/^geonames:\d+$/.test(place.id) || typeof place.label !== "string" || !place.label.trim() || place.label.length > 80)) {
          throw new Error("Invalid place response");
        }
        places = result.places;
        if (!validationError) failure();
        render(op, activate);
      } catch (_) {
        if (!live(op) || controller.signal.aborted) return;
        close(); status.textContent = ""; retry.hidden = false;
        failure("Places couldn’t load. Try again, or leave Location blank.");
      } finally {
        if (live(op)) { request = null; input.setAttribute("aria-busy", "false"); }
      }
    }
    function edited() {
      if (syncIdentity()) return;
      selected = null; dismiss(); failure();
      if (composing || !searchable()) return;
      timer = clock.setTimeout(() => { timer = null; void search(); }, debounceMs);
    }
    function fields() {
      return {location: text() || null, location_place_id: selected && input.value === selected.label ? selected.id : null};
    }
    function validate() {
      if (syncIdentity()) return false;
      if (!text() || text() === saved || (selected && input.value === selected.label)) { failure(); return true; }
      failure("Choose a place from the suggestions, or clear Location to leave it blank.", true);
      input.focus({preventScroll: true}); return false;
    }
    input.addEventListener("input", edited);
    input.addEventListener("compositionstart", () => { composing = true; selected = null; dismiss(); });
    input.addEventListener("compositionend", () => { composing = false; edited(); });
    input.addEventListener("focus", () => {
      syncIdentity();
      if (validationError) return;
      if (!selected && text() !== saved && searchable() && !places.length && !request && timer === null) edited();
    });
    input.addEventListener("keydown", event => {
      if (event.isComposing || event.keyCode === 229 || composing || input.disabled) return;
      if (event.key === "Escape" && (places.length || timer !== null || request)) {
        event.preventDefault(); event.stopPropagation(); dismiss(); return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (!searchable()) return;
        event.preventDefault();
        if (!places.length) { void search(event.key === "ArrowDown" ? "first" : "last"); return; }
        highlight(event.key === "ArrowDown" ? (active + 1) % places.length : (active <= 0 ? places.length - 1 : active - 1));
      } else if (event.key === "Enter" && places.length) {
        event.preventDefault();
        if (active >= 0) list.children[active].click();
        else validate();
      }
    });
    root.addEventListener("focusout", event => { if (!root.contains(event.relatedTarget)) dismiss(); });
    retry.addEventListener("click", () => { input.focus({preventScroll: true}); void search(); });
    syncIdentity();
    return Object.freeze({syncIdentity, load, dismiss, fields, validate, reset() {identity = null; load("");}});
  }
  return {create};
});
